// The ?redeem=1 entry point, and surviving a sign-in redirect.
//
// Geezword sends a teacher two things through two channels: this link, and
// their code privately. The link only says "open the redeem dialog" — it never
// carries the code.
//
// What is stored is a FLAG, never a code. Google sign-in falls back to
// signInWithRedirect when a popup is blocked, which reloads the page and loses
// every bit of React state; the flag is what lets the app reopen the dialog
// afterwards. A code kept in sessionStorage would outlive the tab, be readable
// by any script on the origin, and survive in a crash dump — so the teacher
// retypes it after a redirect. Sixteen characters is a small price for a
// credential never touching storage.

const KEY = 'sk_pending_redeem'
const PARAM = 'redeem'
// Long enough for a Google sign-in, short enough that a shared machine does
// not reopen the dialog for whoever sits down next.
const TTL_MS = 10 * 60 * 1000

function store() {
  try { return globalThis.sessionStorage } catch { return null }
}

/** Mark that the teacher is partway through redeeming. No code is stored. */
export function setPendingRedeem(now = Date.now()) {
  try { store()?.setItem(KEY, String(now)) } catch { /* storage unavailable */ }
}

export function clearPendingRedeem() {
  try { store()?.removeItem(KEY) } catch { /* storage unavailable */ }
}

/**
 * Whether a redemption is pending, clearing it either way so it fires once.
 * Returns false for a stale flag rather than reopening a dialog the teacher
 * abandoned ten minutes ago.
 */
export function consumePendingRedeem(now = Date.now()) {
  let raw = null
  try { raw = store()?.getItem(KEY) ?? null } catch { return false }
  if (raw === null) return false
  clearPendingRedeem()
  const at = Number(raw)
  return Number.isFinite(at) && now - at < TTL_MS
}

/**
 * Read ?redeem from the landing URL and remove it immediately.
 *
 * The parameter is stripped with replaceState so it does not sit in the
 * address bar, reach the next page as a referrer, or end up in the back
 * button's history — the same hygiene a code would need, applied even though
 * the link carries no code, because a teacher may paste one into the URL
 * themselves out of habit.
 */
export function captureRedeemEntry(loc = globalThis.location, hist = globalThis.history) {
  let params
  try { params = new URLSearchParams(loc?.search || '') } catch { return false }
  if (!params.has(PARAM)) return false

  setPendingRedeem()
  params.delete(PARAM)
  try {
    const query = params.toString()
    hist?.replaceState?.({}, '', loc.pathname + (query ? '?' + query : '') + (loc.hash || ''))
  } catch { /* history unavailable; the flag is already set */ }
  return true
}
